import React from "react";
import { getSkuPriceData, resolveDiscount } from "../../helper/discount";


/**
 * Usage variants:
 * <ProductPrices product={product} />
 * or
 * <ProductPrices regularPrice={19.99} salePrice={13.99} fromDate="..." toDate="..." />
 */
// function ProductListPrice(props) {
//     // support both shapes
//     const product = props.product ?? {};
//     const regularPriceRaw = props.regularPrice ?? product.price ?? product.listPrice ?? product.regularPrice;
//     const salePriceRaw = props.salePrice ?? product.salePrice ?? product.discountPrice ?? product.sale_price;
//     const fromDateRaw = props.fromDate ?? product.fromDate ?? product.from_date;
//     const toDateRaw = props.toDate ?? product.toDate ?? product.to_date;

//     // coerce to numbers if strings
//     const regularPrice = typeof regularPriceRaw === "string" ? Number(regularPriceRaw) : regularPriceRaw;
//     const salePrice = typeof salePriceRaw === "string" ? Number(salePriceRaw) : salePriceRaw;

//     const hasSale = salePrice != null && !Number.isNaN(salePrice) && Number(salePrice) > 0;

//     // parse dates with dayjs; handle missing dates
//     const now = dayjs();
//     const start = fromDateRaw ? dayjs(fromDateRaw).startOf("day") : null;
//     const end = toDateRaw ? dayjs(toDateRaw).endOf("day") : null;


//     // inclusive check using day boundaries to avoid timezone issues
//     const isSaleActive = hasSale && (
//         (start && end && (now.isSame(start) || now.isSame(end) || (now.isAfter(start) && now.isBefore(end)))) ||
//         (start && !end && (now.isSame(start) || now.isAfter(start))) ||
//         (!start && end && (now.isSame(end) || now.isBefore(end)))
//     );

//     // DEBUG: log the computed values (remove in production)
//     // Open browser console to see these values if price isn't showing.
//     // eslint-disable-next-line no-console
//     console.debug("ProductPrices debug:",
//         { regularPriceRaw, salePriceRaw, regularPrice, salePrice, fromDateRaw, toDateRaw, start: start?.toString(), end: end?.toString(), now: now.toString(), isSaleActive });

//     return (
//         <div className="ProductPrices sale prod-collection">
//             {isSaleActive ? (
//                 <>
//                     <h4 className="regular oldprice"><del>{regularPrice?.toFixed(2)}€</del></h4>
//                     <h4 className="regular salening">{salePrice?.toFixed(2)}€</h4>
//                 </>
//             ) : (
//                 regularPrice > 0 ? (
//                     <h4 className="regular">{regularPrice?.toFixed(2)}€</h4>
//                 ) : (
//                     <h4 className="regular">Sold Out</h4>
//                 )
//             )}
//         </div>
//     );
// }


function findSkuFromProduct(product, { skuId, variantIndex, sizeIndex } = {}) {
    if (!product) return null;

    // Try skuId first (most explicit)
    if (skuId) {
        for (const variant of product.variants || []) {
            for (const size of variant.sizes || []) {
                const found = (size.skus || []).find(s => s.skuId === skuId || s._id === skuId);
                if (found) return found;
            }
        }
    }

    // Try variantIndex/sizeIndex if provided
    if (typeof variantIndex === "number") {
        const variant = product.variants?.[variantIndex];
        if (variant && typeof sizeIndex === "number") {
            return variant.sizes?.[sizeIndex]?.skus?.[0] ?? null;
        }
        // fallback to first sku of variant
        return variant?.sizes?.[0]?.skus?.[0] ?? null;
    }

    // Default fallback: first SKU with quantity > 0
    for (const variant of product.variants || []) {
        for (const size of variant.sizes || []) {
            const sku = (size.skus || []).find(s => (s.quantity ?? 0) > 0);
            if (sku) return sku;
        }
    }

    // Final fallback: just return first sku anywhere
    for (const variant of product.variants || []) {
        for (const size of variant.sizes || []) {
            if ((size.skus || []).length) return size.skus[0];
        }
    }

    return null;
}

export default function ProductListPrice(props) {
    // allow passing explicit sku or product
    const product = props.product ?? {};
    const skuProp = props.sku ?? props.skuId ?? null;

    // If user passed a SKU object directly
    const explicitSkuObject = skuProp && typeof skuProp === "object" ? skuProp : null;
    const sku = explicitSkuObject || findSkuFromProduct(product, { skuId: skuProp, variantIndex: props.variantIndex, sizeIndex: props.sizeIndex });

    const skuPriceData = getSkuPriceData(sku || product);
    const { regular: regularPrice, sale: salePrice, sale2: salePrice2, hasDiscount, hasDiscount2 } = resolveDiscount({
        price: props.regularPrice ?? skuPriceData.price ?? product.regularPrice,
        discountPrice: props.salePrice ?? skuPriceData.discountPrice ?? product.salePrice ?? product.sale_price,
        discountPrice2: props.salePrice2 ?? skuPriceData.discountPrice2,
        fromDate: props.fromDate ?? skuPriceData.fromDate,
        toDate: props.toDate ?? skuPriceData.toDate,
        fromDate2: props.fromDate2 ?? skuPriceData.fromDate2,
        toDate2: props.toDate2 ?? skuPriceData.toDate2,
    });

    return (
        <div className={`ProductPrices sale prod-collection ${hasDiscount ? "on-sale" : ""}`}>
            {hasDiscount2 ? (
                <>
                    <div className="price-row">
                        <h4 className="regular oldprice"><del>{Number(regularPrice).toFixed(2)}€</del></h4>
                        <h4 className="regular oldprice mx-1"><del>{Number(salePrice).toFixed(2)}€</del></h4>
                        <h4 className="regular salening">{Number(salePrice2).toFixed(2)}€</h4>
                        {/* Badge removed - DiscountBadge component handles badges */}
                    </div>
                </>
            ) : hasDiscount ? (
                <>
                    <div className="price-row">
                        <h4 className="regular oldprice"><del>{Number(regularPrice).toFixed(2)}€</del></h4>
                        <h4 className="regular salening">{Number(salePrice).toFixed(2)}€</h4>
                        {/* Badge removed - DiscountBadge component handles badges */}
                    </div>
                </>
            ) : (
                regularPrice > 0 ? (
                    <h4 className="regular">{Number(regularPrice).toFixed(2)}€</h4>
                ) : (
                    <h4 className="regular">Sold Out</h4>
                )
            )}
        </div>
    );
}


